import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import DatePicker from 'bento-ordering/base/ui_components/DatePicker';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Select from 'components/Select';
import {
  getMenuItemNamesColor,
  getButtonBackgroundHoverColor,
  getHeaderAndNavigationTypography
} from 'styles/helpers/getThemeStyles';
import {
  DEADLINE_CONTROL_TITLE,
  DEADLINE_CONTROL_STRING,
  NO_DEADLINE
} from 'bento-ordering/group_ordering/constants/groupOrder.js';
import {
  timeIntervals,
  getDateSelectOptionsForNumDays
} from 'bento-ordering/group_ordering/utils';

const TitleTypography = styled(Typography)(({ theme }) => ({
  color: getMenuItemNamesColor(theme),
  fontSize: '14px',
  fontWeight: '500'
}));

const TitleWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap'
});

const Container = styled('div')({
  marginTop: '2rem'
});

const DeadlineControlTypography = styled(Typography)({
  marginTop: '.5rem',
  marginBottom: '.5rem'
});

const NoDeadlineTypography = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  textAlign: 'center',
  marginBottom: '1rem',
  fontColor: getButtonBackgroundHoverColor(theme),
  ...getHeaderAndNavigationTypography(theme),
  cursor: 'pointer'
}));

const SelectContainer = styled(Box)({
  display: 'inline-block',
  width: '45%',
  marginLeft: '.5rem',
  marginRight: '.5rem'
});

const GroupOrderDeadlineControl = ({
  inputDeadlineObj,
  existingDeadlineObj,
  setInputDeadlineObj,
  startExpand = false,
  userUpdatedDate,
  userUpdatedTime,
  setUserUpdatedDate,
  setUserUpdatedTime
}) => {
  const [expanded, setExpanded] = useState(startExpand);

  // display existing deadline initially in update GroupOrderSettings scenario
  const deadlineDate = userUpdatedDate
    ? inputDeadlineObj.date
    : existingDeadlineObj.date;
  const deadlineTime = userUpdatedTime
    ? inputDeadlineObj.time
    : existingDeadlineObj.time;

  const handleDateChange = newDate => {
    setInputDeadlineObj({
      ...inputDeadlineObj,
      date: newDate
    });
    setUserUpdatedDate(true);
  };

  const handleTimeChange = newTime => {
    setInputDeadlineObj({
      ...inputDeadlineObj,
      time: newTime
    });
    setUserUpdatedTime(true);
  };

  const handleDeadlineReset = () => {
    setInputDeadlineObj({ date: '', time: '' });
    setUserUpdatedDate(true);
    setUserUpdatedTime(true);
  };

  return (
    <Container>
      <TitleWrapper>
        <AccessAlarmIcon />
        <TitleTypography>{DEADLINE_CONTROL_TITLE}</TitleTypography>
        {expanded ? (
          <ExpandLessIcon
            onClick={() => setExpanded(!expanded)}
            sx={{ cursor: 'pointer' }}
            data-testid="CollapseIcon"
          />
        ) : (
          <ExpandMoreIcon
            data-testid="ExpandMoreIcon"
            onClick={() => setExpanded(!expanded)}
            sx={{ cursor: 'pointer' }}
          />
        )}
      </TitleWrapper>
      <DeadlineControlTypography>
        {DEADLINE_CONTROL_STRING}
      </DeadlineControlTypography>
      {expanded && (
        <div>
          <SelectContainer mb={2.5}>
            <DatePicker
              id="deadline-date"
              name="deadline-date"
              value={deadlineDate}
              options={getDateSelectOptionsForNumDays(7)}
              label="Date"
              onDateChange={handleDateChange}
            />
          </SelectContainer>
          <SelectContainer mb={2.5}>
            <Select
              variant="filled"
              autoWidth={false}
              value={deadlineTime}
              onChange={e => handleTimeChange(e.target.value)}
              id="deadline-time"
              name="deadline-time"
              options={timeIntervals(deadlineDate)}
              label={'Time'}
            />
          </SelectContainer>
          <NoDeadlineTypography onClick={handleDeadlineReset}>
            {NO_DEADLINE}
          </NoDeadlineTypography>
        </div>
      )}
    </Container>
  );
};

GroupOrderDeadlineControl.propTypes = {
  inputDeadlineObj: PropTypes.object.isRequired,
  existingDeadlineObj: PropTypes.object.isRequired,
  setInputDeadlineObj: PropTypes.func.isRequired,
  userUpdatedDate: PropTypes.bool.isRequired,
  userUpdatedTime: PropTypes.bool.isRequired,
  setUserUpdatedDate: PropTypes.func.isRequired,
  setUserUpdatedTime: PropTypes.func.isRequired,
  startExpand: PropTypes.bool.isRequired
};

export default GroupOrderDeadlineControl;
